import React, { useState } from 'react';
import { BookOpen, Download, Play, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

const SampleWorkflows = ({ onLoadWorkflow }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const sampleWorkflows = [
    {
      id: 'water-cycle-lesson',
      name: 'Water Cycle Complete Lesson',
      description: 'Generate a comprehensive water cycle lesson with story, visuals, worksheets, and audio for multiple grades',
      category: 'Science',
      difficulty: 'Beginner',
      estimatedTime: '5-7 minutes',
      outputs: ['Story', 'Visual Diagram', 'Multi-grade Worksheets', 'Audio Content'],
      nodes: [
        {
          id: 'prompt-1',
          type: 'prompt',
          position: { x: 100, y: 100 },
          data: {
            label: 'Water Cycle Prompt',
            config: {
              prompt: 'Create educational content about the water cycle for students. Focus on evaporation, condensation, precipitation, and collection.',
              variables: ['topic']
            }
          }
        },
        {
          id: 'textgen-1', 
          type: 'textGen',
          position: { x: 350, y: 100 },
          data: {
            label: 'Generate Story',
            config: {
              concept: 'water cycle',
              gradeLevel: '4',
              language: 'English'
            }
          }
        },
        {
          id: 'visual-1',
          type: 'visual',
          position: { x: 600, y: 100 },
          data: {
            label: 'Create Diagram',
            config: {
              concept: 'water cycle diagram',
              style: 'educational'
            }
          }
        },
        {
          id: 'worksheet-1',
          type: 'worksheet',
          position: { x: 350, y: 250 },
          data: {
            label: 'Multi-grade Worksheets',
            config: {
              concept: 'water cycle',
              grades: ['3', '4', '5'],
              language: 'English'
            }
          }
        },
        {
          id: 'audio-1',
          type: 'audio',
          position: { x: 600, y: 250 },
          data: {
            label: 'Story Audio',
            config: {
              language: 'English',
              voice: 'default'
            }
          }
        }
      ],
      edges: [
        { id: 'e1-2', source: 'prompt-1', target: 'textgen-1' },
        { id: 'e2-3', source: 'textgen-1', target: 'visual-1' },
        { id: 'e2-4', source: 'textgen-1', target: 'worksheet-1' },
        { id: 'e2-5', source: 'textgen-1', target: 'audio-1' }
      ]
    },
    {
      id: 'research-based-content',
      name: 'Research-Enhanced Learning Content',
      description: 'Create content by first researching from Wikipedia, then generating educational materials',
      category: 'Research',
      difficulty: 'Intermediate', 
      estimatedTime: '6-8 minutes',
      outputs: ['Research Summary', 'Educational Story', 'Visual Content', 'Worksheets'],
      nodes: [
        {
          id: 'api-1',
          type: 'api',
          position: { x: 100, y: 100 },
          data: {
            label: 'Wikipedia Research',
            config: {
              apiType: 'wikipedia',
              query: 'solar system',
              language: 'en'
            }
          }
        },
        {
          id: 'textgen-1',
          type: 'textGen',
          position: { x: 350, y: 100 },
          data: {
            label: 'Create Educational Content',
            config: {
              gradeLevel: '5',
              language: 'English'
            }
          }
        },
        {
          id: 'visual-1',
          type: 'visual',
          position: { x: 600, y: 100 },
          data: {
            label: 'Create Visuals',
            config: {
              style: 'diagram'
            }
          }
        },
        {
          id: 'worksheet-1',
          type: 'worksheet',
          position: { x: 350, y: 250 },
          data: {
            label: 'Generate Worksheets',
            config: {
              grades: ['4', '5', '6'],
              language: 'English'
            }
          }
        }
      ],
      edges: [
        { id: 'e1-2', source: 'api-1', target: 'textgen-1' },
        { id: 'e2-3', source: 'textgen-1', target: 'visual-1' },
        { id: 'e2-4', source: 'textgen-1', target: 'worksheet-1' }
      ]
    },
    {
      id: 'multilingual-content',
      name: 'Multilingual Content Creation',
      description: 'Create the same educational content in multiple languages with audio support',
      category: 'Language',
      difficulty: 'Advanced',
      estimatedTime: '8-10 minutes',
      outputs: ['English Content', 'Hindi Content', 'Marathi Content', 'Audio in All Languages'],
      nodes: [
        {
          id: 'prompt-1',
          type: 'prompt',
          position: { x: 100, y: 100 },
          data: {
            label: 'Base Content Prompt',
            config: {
              prompt: 'Create educational content about {{topic}} suitable for grade {{grade}} students',
              variables: ['topic', 'grade']
            }
          }
        },
        {
          id: 'textgen-english',
          type: 'textGen',
          position: { x: 300, y: 50 },
          data: {
            label: 'English Content',
            config: {
              concept: 'mathematics basics',
              gradeLevel: '3',
              language: 'English'
            }
          }
        },
        {
          id: 'textgen-hindi',
          type: 'textGen',
          position: { x: 300, y: 150 },
          data: {
            label: 'Hindi Content',
            config: {
              concept: 'mathematics basics',
              gradeLevel: '3',
              language: 'Hindi'
            }
          }
        },
        {
          id: 'textgen-marathi',
          type: 'textGen',
          position: { x: 300, y: 250 },
          data: {
            label: 'Marathi Content',
            config: {
              concept: 'mathematics basics',
              gradeLevel: '3',
              language: 'Marathi'
            }
          }
        },
        {
          id: 'audio-english',
          type: 'audio',
          position: { x: 550, y: 50 },
          data: {
            label: 'English Audio',
            config: {
              language: 'English',
              voice: 'default'
            }
          }
        },
        {
          id: 'audio-hindi',
          type: 'audio',
          position: { x: 550, y: 150 },
          data: {
            label: 'Hindi Audio',
            config: {
              language: 'Hindi',
              voice: 'default'
            }
          }
        },
        {
          id: 'audio-marathi',
          type: 'audio',
          position: { x: 550, y: 250 },
          data: {
            label: 'Marathi Audio',
            config: {
              language: 'Marathi',
              voice: 'default'
            }
          }
        }
      ],
      edges: [
        { id: 'e1-en', source: 'prompt-1', target: 'textgen-english' },
        { id: 'e1-hi', source: 'prompt-1', target: 'textgen-hindi' },
        { id: 'e1-mr', source: 'prompt-1', target: 'textgen-marathi' },
        { id: 'e-en-audio', source: 'textgen-english', target: 'audio-english' },
        { id: 'e-hi-audio', source: 'textgen-hindi', target: 'audio-hindi' },
        { id: 'e-mr-audio', source: 'textgen-marathi', target: 'audio-marathi' }
      ]
    },
    {
      id: 'interactive-geography',
      name: 'Interactive Geography Lesson',
      description: 'Research country information and create comprehensive geography content',
      category: 'Geography',
      difficulty: 'Intermediate',
      estimatedTime: '5-6 minutes',
      outputs: ['Country Facts', 'Educational Story', 'Maps & Visuals', 'Quiz Worksheets'],
      nodes: [
        {
          id: 'input-1',
          type: 'input',
          position: { x: 100, y: 100 },
          data: {
            label: 'Country Input',
            config: {
              inputType: 'text',
              placeholder: 'Enter country name'
            }
          }
        },
        {
          id: 'api-country',
          type: 'api',
          position: { x: 300, y: 100 },
          data: {
            label: 'Country Research',
            config: {
              apiType: 'country'
            }
          }
        },
        {
          id: 'textgen-facts',
          type: 'textGen',
          position: { x: 500, y: 50 },
          data: {
            label: 'Educational Content',
            config: {
              gradeLevel: '4',
              language: 'English'
            }
          }
        },
        {
          id: 'visual-map',
          type: 'visual',
          position: { x: 500, y: 150 },
          data: {
            label: 'Create Maps/Visuals',
            config: {
              style: 'educational'
            }
          }
        },
        {
          id: 'worksheet-quiz',
          type: 'worksheet',
          position: { x: 700, y: 100 },
          data: {
            label: 'Geography Quiz',
            config: {
              grades: ['4', '5'],
              language: 'English'
            }
          }
        }
      ],
      edges: [
        { id: 'e1-country', source: 'input-1', target: 'api-country' },
        { id: 'e-country-text', source: 'api-country', target: 'textgen-facts' },
        { id: 'e-country-visual', source: 'api-country', target: 'visual-map' },
        { id: 'e-text-quiz', source: 'textgen-facts', target: 'worksheet-quiz' }
      ]
    }
  ];

  const loadWorkflow = (workflow) => {
    onLoadWorkflow(workflow);
    toast.success(`Loaded "${workflow.name}" workflow!`);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Science': 'bg-blue-100 text-blue-800',
      'Research': 'bg-purple-100 text-purple-800',
      'Language': 'bg-pink-100 text-pink-800',
      'Geography': 'bg-green-100 text-green-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-4 overflow-y-auto max-h-full">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Sample Workflows</h2>
        <p className="text-gray-600">Get started quickly with pre-built educational workflow templates</p>
      </div>

      <div className="space-y-4">
        {sampleWorkflows.map((workflow) => (
          <div
            key={workflow.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="p-2 w-full">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{workflow.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{workflow.description}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(workflow.category)}`}>
                  {workflow.category}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(workflow.difficulty)}`}>
                  {workflow.difficulty}
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  ⏱️ {workflow.estimatedTime}
                </span>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Expected Outputs:</h4>
                <div className="flex flex-wrap gap-1">
                  {workflow.outputs.map((output, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                      {output}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {workflow.nodes.length} blocks, {workflow.edges.length} connections
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedWorkflow(selectedWorkflow === workflow.id ? null : workflow.id)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                  >
                    <Eye className="w-4 h-4" />
                    <span>{selectedWorkflow === workflow.id ? 'Hide' : 'Preview'}</span>
                  </button>
                  <button
                    onClick={() => loadWorkflow(workflow)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    <Download className="w-4 h-4" />
                    <span>Load</span>
                  </button>
                </div>
              </div>

              {selectedWorkflow === workflow.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Workflow Structure:</h4>
                  <div className="bg-gray-50 rounded p-3 text-xs">
                    <div className="mb-2">
                      <strong>Nodes ({workflow.nodes.length}):</strong>
                      <ul className="mt-1 space-y-1">
                        {workflow.nodes.map((node, index) => (
                          <li key={index} className="text-gray-600">
                            • {node.data.label} ({node.type})
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <strong>Flow:</strong>
                      <p className="mt-1 text-gray-600">
                        {workflow.edges.map((edge, index) => (
                          <span key={index}>
                            {workflow.nodes.find(n => n.id === edge.source)?.data.label} → {workflow.nodes.find(n => n.id === edge.target)?.data.label}
                            {index < workflow.edges.length - 1 && ', '}
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-medium text-blue-900 mb-2">💡 Tips for Using Sample Workflows</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Click any node after loading to customize its configuration</li>
          <li>• Modify prompts, languages, and grade levels to suit your needs</li>
          <li>• Add or remove connections between nodes to change the flow</li>
          <li>• Save your customized workflows for future use</li>
          <li>• Make sure all required agents are running before executing workflows</li>
        </ul>
      </div>
    </div>
  );
};

export default SampleWorkflows;